import { useAppDispatch, useAppSelector } from "@SonarCX/hooks/redux";
import { Tab, Tabs } from "@mui/material";
import React from "react";

import CopyableStateProvider from "@SonarCX/providers/CopyableStateProvider";
import TaskCommentsEditProvider from "@SonarCX/providers/TaskCommentEditProvider";

import TaskCommentInput from "../../../../atoms/TaskCommentInput";
import TaskActivity from "./TaskActivity";
import TaskComment from "./TaskComment";
import TimelineEmail, { TimelineEmailType } from "./TimelineEmail";

import { useEnvironment } from "@SonarCX/providers/EnvironmentProvider";
import { useTaskAPI } from "@SonarCX/providers/TaskAPIProvider";
import { mockComment } from "@SonarCX/providers/TaskAPIProvider/helpers";
import { useTaskComments } from "@SonarCX/providers/TaskCommentsProvider";
import { loadTaskActivities, selectActivity } from "@SonarCX/slices/activity";
import {
  EMAIL_FILE_EXTENSION,
  loadEmailContent,
  selectExtensionAPI,
} from "@SonarCX/slices/extensionAPI";
import useStyles from "./styles";
import useSavedValue from "@SonarCX/hooks/useSavedValue";

const ALL = "0";
const COMMENTS_ONLY = "1";
const AUDIT_ONLY = "2";

type HydratedEmail = {
  type: "email";
  data: TimelineEmailType;
};

type TaskCommentsProps = {
  comments?: APIComment[];
  submitComment: (comment: string) => void;
  canComment?: boolean;
  taskID: string;
};

const tabs = ["ALL EVENTS", "COMMENTS ONLY", "ACTIVITY ONLY"];

const TaskComments: React.FC<TaskCommentsProps> = ({
  comments: unsortedComments = [],
  submitComment,
  canComment,
  taskID,
}) => {
  const dispatch = useAppDispatch();
  const { task: allTaskActivity, loaded } = useAppSelector(selectActivity);
  const { loadedEmails } = useAppSelector(selectExtensionAPI);

  const { processCommentEvent } = useTaskComments();
  const { user } = useEnvironment();
  const { tasks } = useTaskAPI();

  const [comment, setComment] = React.useState("");
  const [tab, setTab] = useSavedValue<string>({
    key: "taskUpdatesTab",
    defaultValue: ALL,
  });

  const comments = React.useMemo(() => {
    const sorted = unsortedComments.sort(
      (a, b) => +new Date(b.stamp) - +new Date(a.stamp),
    );
    return sorted.map((comment) => ({ type: "comment", data: comment }));
  }, [unsortedComments]);

  const activities = React.useMemo(() => {
    if (!loaded.includes(taskID)) {
      return [];
    }

    return allTaskActivity[taskID].map((activity) => ({
      type: "activity",
      data: activity,
    }));
  }, [loaded, taskID, allTaskActivity]);

  const emails = React.useMemo(() => {
    const task = tasks[taskID];

    if (!task) {
      return [];
    }

    const media = task.media ?? [];

    return media.filter((m) => m.filename.endsWith(EMAIL_FILE_EXTENSION));
  }, [tasks, taskID]);

  // Load email content from stored media
  React.useEffect(() => {
    emails.forEach((email) => {
      dispatch(loadEmailContent(email));
    });
  }, [emails, dispatch]);

  const hydratedEmails = React.useMemo(() => {
    const hydrated: HydratedEmail[] = [];

    emails.forEach((email) => {
      const content = loadedEmails[email.id];

      if (content) {
        hydrated.push({
          type: "email",
          data: {
            ...content,
            stamp: new Date(content.receivedTime),
            id: email.id,
          },
        });
      }
    });

    return hydrated;
  }, [emails, loadedEmails]);

  const combined = React.useMemo(() => {
    if (tab === AUDIT_ONLY) {
      return activities;
    }

    if (tab === COMMENTS_ONLY) {
      return [...comments, ...hydratedEmails];
    }

    return [...activities, ...comments, ...hydratedEmails];
  }, [tab, activities, comments, hydratedEmails]);

  const sortedCombined = React.useMemo(() => {
    return [...combined].sort(
      (a, b) => +new Date(b.data.stamp) - +new Date(a.data.stamp),
    );
  }, [combined]);

  const classes = useStyles();

  // Loads task activities
  React.useEffect(() => {
    if (taskID) dispatch(loadTaskActivities(taskID));
  }, [dispatch, taskID]);

  const handleSubmit = () => {
    const trimmed = comment.trim();

    if (trimmed.length) {
      const mocked = mockComment(trimmed, user?.id, taskID);
      processCommentEvent({ comment: mocked });
      setComment("");
      submitComment(trimmed);
    }
  };

  return (
    <div className={classes.root}>
      <Tabs
        value={+tab}
        onChange={(event) => {
          const name = event.currentTarget.textContent;
          setTab(tabs.indexOf(name).toString() ?? ALL);
        }}
        className={classes.tabs}
        centered
      >
        {tabs.map((label) => (
          <Tab key={label} label={label} classes={{ root: classes.tab }} />
        ))}
      </Tabs>

      <TaskCommentsEditProvider taskID={taskID}>
        {[ALL, COMMENTS_ONLY].includes(tab) && (
          <div className={classes.inputHolder}>
            <TaskCommentInput
              value={comment}
              onChange={setComment}
              canComment={canComment}
              submit={handleSubmit}
              className={classes.input}
            />
          </div>
        )}

        <div className={classes.comments}>
          {sortedCombined.map((event) => {
            const key = event.data.id;

            if (event.type === "comment") {
              const data = event.data as APIComment;
              return (
                <CopyableStateProvider key={key}>
                  <TaskComment comment={data} inModal onDetails />
                </CopyableStateProvider>
              );
            }

            if (event.type === "email") {
              const data = event.data as HydratedEmail["data"];
              return <TimelineEmail key={key} email={data} />;
            }

            const data = event.data as TaskActivityResponse;
            return <TaskActivity key={key} activity={data} />;
          })}
        </div>
      </TaskCommentsEditProvider>
    </div>
  );
};

export default TaskComments;
